import { useCallback, useEffect, useRef, useState } from 'react';
import { useHoverNavigationContext } from './hover-navigation-context';
import { useUserAppsContext } from './user-apps-context';
import { useNavigationMode } from './navigation-mode-context';

/** Default time in ms each item stays highlighted before moving to the next */
export const DEFAULT_HOVER_DWELL_TIME_MS = 2000;

/**
 * Hook that powers keyboard-accessible scanning navigation.
 * Active state is shared via HoverNavigationContext so it persists across pages.
 * Cycles through items at a configurable interval (from user settings) and pressing "Enter" triggers onSelect.
 * Only works when navigation mode is "hover" - disabled when "xy_cursor" is active.
 *
 * @param {number} itemCount  Number of selectable items
 * @param {(index: number) => void} onSelect  Called when Enter is pressed on the active item
 */
export default function useHoverNavigation(itemCount, onSelect) {
    const { active, toggle } = useHoverNavigationContext();
    const { userSettings } = useUserAppsContext();
    const { navigationMode } = useNavigationMode();
    const [activeIndex, setActiveIndex] = useState(0);
    const [paused, setPaused] = useState(false);
    const onSelectRef = useRef(onSelect);
    onSelectRef.current = onSelect;

    // Use dwell time from user settings, fallback to default
    const dwellTime = userSettings?.dwell_time || DEFAULT_HOVER_DWELL_TIME_MS;

    // Only allow hover navigation when mode is "hover"
    const isHoverMode = navigationMode === 'hover';
    const effectivelyActive = active && isHoverMode;

    const pause = useCallback(() => setPaused(true), []);
    const resume = useCallback(() => setPaused(false), []);

    // Reset index when entering a page or when mode is toggled off then on
    useEffect(() => {
        setActiveIndex(0);
    }, [effectivelyActive]);

    // Cycle through items (stops when paused or not in hover mode)
    useEffect(() => {
        if (!effectivelyActive || paused || itemCount === 0) return;
        const id = setInterval(() => {
            setActiveIndex((prev) => (prev + 1) % itemCount);
        }, dwellTime);
        return () => clearInterval(id);
    }, [effectivelyActive, paused, itemCount, dwellTime]);

    // Listen for Enter key (only in hover mode)
    useEffect(() => {
        if (!isHoverMode) return; // Don't interfere with xy_cursor mode

        const handler = (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                if (paused) {
                    resume();
                } else if (active) {
                    onSelectRef.current(activeIndex);
                } else {
                    toggle();
                }
                setActiveIndex(0);
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [isHoverMode, active, activeIndex, paused, resume, toggle]);

    return { active: effectivelyActive, activeIndex, paused, pause, resume, toggle };
}
